/**
 * Wealthica2 cache management utilities
 *
 * Responsibilities:
 * - Define cache keys and cross-session persistence policy (accounts/positions)
 * - Navigation-aware read-through for fast UI hydration
 * - Daily refresh checks, orphaned cache cleanup, and re-keying on reconnect
 *
 * Notes:
 * - Uses `WealthicaStorage` for persistence (session/local as appropriate)
 * - Pure frontend cache operations; does not mutate backend state
 */
import { WealthicaStorage } from '../utils/storage';
import { logger } from '@/services/LoggingService';
import { 
  GLOBAL_CACHE_TTL_MS, 
  CacheData, 
  createCacheData, 
  isCacheFresh, 
  getCacheAge 
} from '@/utils/GlobalCacheConfig';
import { normalizeInstitutionIdForCache } from './identifiers';

// Wealthica2-specific cache keys
export const CACHE_KEYS = {
  ACCOUNTS: 'wealthica2_accounts_cache',
  POSITIONS: 'wealthica2_positions_cache',
  SESSION_INITIALIZED: 'wealthica2_initialized_session',
  LAST_REFRESH: 'wealthica2_last_refresh'
};

/**
 * Generate institution-specific accounts cache key
 * @param institutionId The institution ID
 * @returns Institution-specific accounts cache key
 */
export function getAccountsCacheKey(institutionId: string): string {
  const normalizedId = normalizeInstitutionIdForCache(institutionId);
  return `${CACHE_KEYS.ACCOUNTS}:${normalizedId}`;
}

/**
 * Generate institution-specific positions cache key
 * @param institutionId The institution ID
 * @returns Institution-specific positions cache key
 */
export function getPositionsCacheKey(institutionId: string): string {
  const normalizedId = normalizeInstitutionIdForCache(institutionId);
  return `${CACHE_KEYS.POSITIONS}:${normalizedId}:all`;
}

/**
 * Writes data to cache with timestamp
 * @param key The cache key
 * @param data The data to cache
 */
export function writeToCache<T>(key: string, data: T): void {
  const cacheData = createCacheData(data);
  logger.info('WealthicaCache', `Writing to cache: ${key}`, {
    dataSize: Array.isArray(data) ? (data?.length || 0) : 'object'
  });
  // Persist accounts and positions cache to localStorage for fast navigation/refresh
  const shouldPersist = key.startsWith(CACHE_KEYS.ACCOUNTS) || key.startsWith(CACHE_KEYS.POSITIONS);
  WealthicaStorage.setItem(key, JSON.stringify(cacheData), shouldPersist);
}

/**
 * Cache accounts data for a specific institution
 * @param institutionId The institution ID
 * @param accounts The accounts data to cache
 */
export function cacheAccountsForInstitution(institutionId: string, accounts: any[]): void {
  const cacheKey = getAccountsCacheKey(institutionId);
  writeToCache(cacheKey, accounts);
  logger.info('WealthicaCache', `Cached accounts for institution: ${institutionId}`, {
    accountCount: accounts?.length || 0,
    cacheKey
  });
}

/**
 * Retrieve cached accounts for a specific institution
 * @param institutionId The institution ID
 * @param isNavigation Whether we're in a navigation flow
 * @returns Cached accounts or null if not available/stale
 */
export function getCachedAccountsForInstitution<T>(institutionId: string, isNavigation: boolean): T[] | null {
  const cacheKey = getAccountsCacheKey(institutionId);
  const cachedAccounts = readFromCache<T[]>(cacheKey, isNavigation);
  
  if (cachedAccounts) {
    logger.info('WealthicaCache', `Retrieved cached accounts for institution: ${institutionId}`, {
      accountCount: cachedAccounts?.length || 0,
      cacheKey
    });
  }
  
  return cachedAccounts;
}

/**
 * Reads data from cache if available and fresh
 * @param key The cache key
 * @param isNavigation Whether we're in a navigation flow
 * @returns The cached data if valid, null otherwise
 */
export function readFromCache<T>(key: string, isNavigation: boolean): T | null {
  if (!isNavigation) {
    logger.info('WealthicaCache', `Initial page load, not using cache for ${key}`);
    return null;
  }
  
  try {
    const cachedStr = WealthicaStorage.getItem(key);
    if (!cachedStr) {
      logger.info('WealthicaCache', `No cache found for ${key}`);
      return null;
    }
    
    const cachedData = JSON.parse(cachedStr);
    const currentTime = new Date().getTime();
    
    // Handle both legacy format and new timestamped format
    if (cachedData && cachedData.timestamp && cachedData.data) {
      if (isCacheFresh(cachedData, GLOBAL_CACHE_TTL_MS)) {
        logger.info('WealthicaCache', `Using cached ${key}`, {
          cacheAge: getCacheAge(cachedData),
        });
        return cachedData.data;
      } else {
        logger.info('WealthicaCache', `Stale cache for ${key}`, {
          cacheAge: getCacheAge(cachedData),
          ttl: `${Math.round(GLOBAL_CACHE_TTL_MS / 1000)}s`
        });
      }
    } else if (Array.isArray(cachedData) || typeof cachedData === 'object') {
      logger.info('WealthicaCache', `Using legacy cache format for ${key}`);
      return cachedData;
    }
  } catch (parseError) {
    logger.error('WealthicaCache', `Error parsing ${key} cache`, { parseError });
  }
  
  return null;
}

/**
 * Clears a specific cache
 * @param key The cache key to clear
 */
export function clearCache(key: string): void {
  logger.info('WealthicaCache', `Clearing cache: ${key}`);
  WealthicaStorage.removeItem(key);
}

/**
 * Clears all Wealthica2 caches
 */
export function clearAllCaches(): void {
  logger.info('WealthicaCache', 'Clearing all Wealthica2 caches');
  Object.values(CACHE_KEYS).forEach(clearCache);
}

/**
 * Clears cache on auth failure (401/406 responses)
 * Provider-specific implementation of cache clearing
 * @param reason The reason for clearing cache
 */
export function clearCacheOnAuthFailure(reason: string): void {
  logger.warn('WealthicaCache', 'Clearing Wealthica2 cache due to auth failure', { reason });
  clearAllCaches();
  
  // Also clear Zustand store persistence to prevent phantom data
  try {
    import('@/utils/zustandPersistenceCleanup').then(({ clearWealthicaStorePersistence }) => {
      clearWealthicaStorePersistence();
      logger.info('WealthicaCache', 'Cleared Zustand persistence after auth failure');
    });
  } catch (error) {
    logger.warn('WealthicaCache', 'Failed to clear Zustand persistence after auth failure', { error });
  }
}

/**
 * Checks if the session has been initialized
 * @returns true if the session is initialized, false otherwise
 */
export function getSessionInitialized(): boolean {
  return WealthicaStorage.getItem(CACHE_KEYS.SESSION_INITIALIZED) === 'true';
}

/**
 * Marks the session as initialized
 */
export function setSessionInitialized(): void {
  WealthicaStorage.setItem(CACHE_KEYS.SESSION_INITIALIZED, 'true');
}

/**
 * Gets the last refresh timestamp
 * @returns The last refresh timestamp or null if not set
 */
export function getLastRefresh(): string | null {
  return WealthicaStorage.getItem(CACHE_KEYS.LAST_REFRESH);
}

/**
 * Sets the last refresh timestamp to now
 */
export function setLastRefresh(): void {
  WealthicaStorage.setItem(CACHE_KEYS.LAST_REFRESH, new Date().toISOString());
}

/**
 * Checks if a daily refresh is needed (more than 24 hours since last refresh)
 * @returns true if refresh is needed, false otherwise
 */
export function isDailyRefreshNeeded(): boolean {
  const lastRefresh = getLastRefresh();
  if (!lastRefresh) {
    return true; // No last refresh recorded, refresh needed
  }
  
  const lastRefreshTime = new Date(lastRefresh).getTime();
  const currentTime = new Date().getTime();
  const timeDiff = currentTime - lastRefreshTime;
  const dayInMs = 24 * 60 * 60 * 1000;
  
  return timeDiff > dayInMs;
}

/**
 * Re-key cache entries from an old institution ID to a new institution ID.
 * This is useful when the provider issues a new connection/institution id for the
 * same logical institution (e.g., reconnect flow).
 *
 * Moves both accounts and positions caches (":all" namespace) and removes old keys.
 *
 * @param oldInstitutionId The previous institution ID
 * @param newInstitutionId The new institution ID
 * @returns Flags indicating which caches were moved
 */
export function rekeyInstitutionCaches(
  oldInstitutionId: string,
  newInstitutionId: string
): { accountsMoved: boolean; positionsMoved: boolean } {
  let accountsMoved = false;
  let positionsMoved = false;

  try {
    const oldAccountsKey = getAccountsCacheKey(oldInstitutionId);
    const newAccountsKey = getAccountsCacheKey(newInstitutionId);
    const oldPositionsKey = getPositionsCacheKey(oldInstitutionId); // includes :all suffix
    const newPositionsKey = getPositionsCacheKey(newInstitutionId);

    // Accounts cache
    const oldAccounts = WealthicaStorage.getItem(oldAccountsKey);
    if (oldAccounts && oldAccountsKey !== newAccountsKey) {
      WealthicaStorage.setItem(newAccountsKey, oldAccounts, true);
      WealthicaStorage.removeItem(oldAccountsKey);
      accountsMoved = true;
      logger.info('WealthicaCache', 'Re-keyed accounts cache', {
        from: oldAccountsKey,
        to: newAccountsKey
      });
    }

    // Positions cache
    const oldPositions = WealthicaStorage.getItem(oldPositionsKey);
    if (oldPositions && oldPositionsKey !== newPositionsKey) {
      WealthicaStorage.setItem(newPositionsKey, oldPositions, true);
      WealthicaStorage.removeItem(oldPositionsKey);
      positionsMoved = true;
      logger.info('WealthicaCache', 'Re-keyed positions cache', {
        from: oldPositionsKey,
        to: newPositionsKey
      });
    }
  } catch (err) {
    logger.warn('WealthicaCache', 'Failed to re-key institution caches', {
      oldInstitutionId,
      newInstitutionId,
      error: err
    });
  }

  return { accountsMoved, positionsMoved };
}

/**
 * Remove accounts/positions caches for institution IDs not present in the provided set.
 * Safe in dev/test: only affects frontend caches, not backend data.
 */
export function cleanupCachesNotIn(activeInstitutionIds: Set<string>): string[] {
  const removed: string[] = [];
  const tryDelete = (key: string) => {
    try {
      WealthicaStorage.removeItem(key);
      removed.push(key);
    } catch {}
  };

  const processStorage = (storage: Storage) => {
    try {
      Object.keys(storage).forEach((key) => {
        if (key.startsWith(CACHE_KEYS.ACCOUNTS)) {
          const match = key.match(/^wealthica2_accounts_cache:([^:]+)$/);
          const id = match?.[1];
          if (id && !activeInstitutionIds.has(id)) {
            tryDelete(key);
          }
        } else if (key.startsWith(CACHE_KEYS.POSITIONS)) {
          const match = key.match(/^wealthica2_positions_cache:([^:]+):/);
          const id = match?.[1];
          if (id && !activeInstitutionIds.has(id)) {
            tryDelete(key);
          }
        }
      });
    } catch {}
  };

  try {
    processStorage(sessionStorage);
    processStorage(localStorage);
    if (removed.length > 0) {
      logger.info('WealthicaCache', 'Cleaned up orphaned institution caches', {
        removedCount: removed.length,
        removed
      });
    }
  } catch {}

  return removed;
}
